import { useState } from "react";
import { collection, doc } from "firebase/firestore";
import { ref as storageRef, uploadBytes } from "firebase/storage";
import { COMPANY_DOC_CATEGORIES, COMPANY_DOC_MAX_BYTES, paths, type CompanyDocCategory } from "@clevis/shared-types";
import { Button, FilePicker, Input, Label, Select } from "@clevis/shared-ui";
import { db, storage } from "../lib/firebase";
import { call, CallError } from "../lib/callFunction";

// Add a company document (W-9, certificate, brochure) right where it's being
// sent, without leaving the page. It lands in the company's library too.

export function QuickDocUpload({ tenantId, customerId, onSaved, onCancel }: { tenantId: string; customerId: string | null; onSaved: (docId: string) => void; onCancel: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState("");
  const [category, setCategory] = useState<CompanyDocCategory>("w9");
  const [expires, setExpires] = useState("");
  const [forThisCustomer, setForThisCustomer] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const cat = COMPANY_DOC_CATEGORIES.find((c) => c.id === category);

  async function save() {
    if (!file) return setError("Choose the file.");
    if (!name.trim()) return setError('Give it a name customers will recognize, like "W-9 (2026)".');
    if (file.size > COMPANY_DOC_MAX_BYTES) return setError("Files can be up to 20 MB.");
    setBusy(true);
    setError("");
    try {
      const docId = doc(collection(db, paths.tenantCompanyDocs(tenantId))).id;
      const safe = file.name.replace(/[^\w.\- ()]+/g, "_").slice(-100) || "document";
      const storagePath = paths.companyDocStorage(tenantId, docId, `${Date.now()}-${safe}`);
      await uploadBytes(storageRef(storage, storagePath), file, { contentType: file.type || "application/octet-stream" });
      await call("saveCompanyDoc", {
        tenantId,
        docId,
        name: name.trim(),
        category,
        folder: "",
        notes: "",
        expiresAt: cat?.expires && expires ? Date.parse(`${expires}T00:00:00Z`) : null,
        forCustomerId: forThisCustomer ? customerId : null,
        storagePath,
      });
      onSaved(docId);
    } catch (err) {
      const e = err as CallError;
      setError(e.hint ? `${e.message} ${e.hint}` : e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-1 space-y-2 rounded-lg border border-steel/20 bg-white p-2">
      <FilePicker
        file={file}
        hint="PDF or a photo, or drop it here"
        onChange={(f) => {
          setFile(f);
          if (f && !name) setName(f.name.replace(/\.[^.]+$/, "").replace(/[_-]+/g, " ").slice(0, 120));
        }}
      />
      <div className="grid gap-2 sm:grid-cols-3">
        <div className="sm:col-span-2">
          <Label>Name</Label>
          <Input className="py-1 text-sm" value={name} maxLength={120} placeholder="W-9 (2026)" onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <Label>Kind</Label>
          <Select className="py-1 text-sm" value={category} onChange={(e) => setCategory(e.target.value as CompanyDocCategory)}>
            {COMPANY_DOC_CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </Select>
        </div>
        {cat?.expires ? (
          <div>
            <Label>Good through</Label>
            <Input type="date" className="py-1 text-sm" value={expires} onChange={(e) => setExpires(e.target.value)} />
          </div>
        ) : null}
        {customerId ? (
          <label className="flex items-end gap-2 pb-1 text-xs text-ink sm:col-span-2">
            <input type="checkbox" checked={forThisCustomer} onChange={(e) => setForThisCustomer(e.target.checked)} />
            Made out to this customer only (like a COI naming them)
          </label>
        ) : null}
      </div>
      {error ? <p className="text-xs text-danger">{error}</p> : null}
      <div className="flex items-center gap-2">
        <Button type="button" className="px-3 py-1 text-xs" busy={busy} onClick={() => void save()}>
          Add and pick it
        </Button>
        <Button type="button" variant="ghost" className="px-3 py-1 text-xs" onClick={onCancel}>
          Cancel
        </Button>
        <span className="text-[11px] text-steel">It's saved to your company documents too.</span>
      </div>
    </div>
  );
}
